import { createContext, useCallback, useContext, useState } from 'react';

/**
 * The DOM node that absolutely-positioned legacy `Sidebar` panels portal into.
 *
 * Defaults to `null` so that a `Sidebar` rendered without a provider falls back
 * to rendering in place. `BaseLayout` provides the main-content stage; `Portal`
 * overrides it with its own full-screen overlay node.
 */
export const SidebarSlotContext = createContext<HTMLElement | null>(null);

export function useSidebarSlot(): HTMLElement | null {
  return useContext(SidebarSlotContext);
}

/**
 * State-backed callback ref for a slot node. Returning the node via state (rather
 * than a plain ref) re-renders the provider once the element mounts, so portaling
 * consumers pick it up. Mirrors the pattern in `utils/portalTarget.tsx`.
 */
export function useSlotNode(): {
  node: HTMLElement | null;
  ref: (node: HTMLElement | null) => void;
} {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const ref = useCallback((next: HTMLElement | null) => setNode(next), []);
  return { node, ref };
}
